import React, { useEffect, useState } from "react";
import { db, auth } from "./firebase";
import { ref, onValue, remove, update, push } from "firebase/database";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import "./AdminPage.css";

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [appointments, setAppointments] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [expenses, setExpenses] = useState([]);
  const [accountsSubTab, setAccountsSubTab] = useState("expenditure");

  // State for recording expenses/transactions
  const [newExpense, setNewExpense] = useState({
    date: new Date().toISOString().slice(0, 10),
    category: "Clinical supplies",
    subCategory: "Gloves, masks, syringes, cotton rolls",
    amount: "",
    paymentMethod: "Cash",
    description: "",
    vendor: "",
    notes: "",
    reference: "",
  });

  const [customSubCategory, setCustomSubCategory] = useState("");

  // Filter states for transaction log
  const [expenseSearch, setExpenseSearch] = useState("");
  const [expenseFilterCategory, setExpenseFilterCategory] = useState("All");
  const [expenseFilterMethod, setExpenseFilterMethod] = useState("All");

  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // State for adding new patient manually
  const [newPatient, setNewPatient] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    time: "",
    notes: "",
    procedure: "Consultation",
    status: "Pending",
    payment: "0",
    pendingPayment: "0",
  });

  // Fetch data from Firebase for the backup tabs (Appointments, Careers & Expenses)
  useEffect(() => {
    setLoading(true);
    let appointmentsLoaded = false;
    let careersLoaded = false;
    let expensesLoaded = false;

    const checkLoading = () => {
      if (appointmentsLoaded && careersLoaded && expensesLoaded) {
        setLoading(false);
      }
    };

    const appointmentRef = ref(db, "appointments");
    const careersRef = ref(db, "careers");
    const expensesRef = ref(db, "expenses");

    // Listen to Appointments
    const unsubAppointments = onValue(
      appointmentRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const list = Object.entries(data).map(([id, item]) => ({
            id,
            ...item,
          }));
          list.sort((a, b) => new Date(b.date) - new Date(a.date));
          setAppointments(list);
        } else {
          setAppointments([]);
        }
        appointmentsLoaded = true;
        checkLoading();
      },
      (error) => {
        console.error("Appointments fetch error:", error);
        appointmentsLoaded = true;
        checkLoading();
      }
    );

    // Listen to Careers
    const unsubCareers = onValue(
      careersRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const list = Object.entries(data).map(([id, item]) => ({
            id,
            ...item,
          }));
          list.sort((a, b) => b.createdAt - a.createdAt);
          setApplications(list);
        } else {
          setApplications([]);
        }
        careersLoaded = true;
        checkLoading();
      },
      (error) => {
        console.error("Careers fetch error:", error);
        careersLoaded = true;
        checkLoading();
      }
    );

    // Listen to Expenses
    const unsubExpenses = onValue(
      expensesRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const list = Object.entries(data).map(([id, item]) => ({
            id,
            ...item,
          }));
          list.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
          setExpenses(list);
        } else {
          setExpenses([]);
        }
        expensesLoaded = true;
        checkLoading();
      },
      (error) => {
        console.error("Expenses fetch error:", error);
        expensesLoaded = true;
        checkLoading();
      }
    );

    return () => {
      unsubAppointments();
      unsubCareers();
      unsubExpenses();
    };
  }, []);

  // Calculate real statistical data from Firebase appointments list
  const getOverviewStats = () => {
    const now = new Date();
    
    // YYYY-MM-DD local format
    const getLocalDateString = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const todayStr = getLocalDateString(now);

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const yesterdayStr = getLocalDateString(yesterday);

    const currentMonthStr = todayStr.slice(0, 7); // "YYYY-MM"

    const normalizeProcedureName = (name) => {
      if (!name) return "Consultation";
      const clean = name.trim().toLowerCase().replace(/\s+/g, "");
      if (clean === "rootcanal" || clean === "rc") return "Root Canal";
      if (clean === "cleaning" || clean === "teethcleaning") return "Teeth Cleaning";
      if (clean === "filling" || clean === "toothfilling") return "Tooth Filling";
      if (clean === "extraction") return "Extraction";
      if (clean === "whitening" || clean === "teethwhitening") return "Teeth Whitening";
      if (clean === "braces" || clean === "bracescheck") return "Braces Check";
      if (clean === "consultation") return "Consultation";
      if (clean === "problempain" || clean === "pain") return "Problem / Pain";
      return name;
    };

    let todayCount = 0;
    let yesterdayCount = 0;
    let todayRevenue = 0;
    let yesterdayRevenue = 0;
    let newPatientsToday = 0;
    let monthlyCount = 0;
    let pendingConfirmations = 0;

    const todayList = [];
    const patientAppointmentsMap = {};
    let canceledCount = 0;
    let noShowCount = 0;

    const procedureCounts = {};
    let totalOutstanding = 0;
    const uniqueDuesPatients = new Set();
    let overdue30DaysCount = 0;

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(now.getDate() - 30);

    let currentMonthRevenue = 0;
    const currentMonthPatients = new Set();

    // Weekly distribution setup: Mon-Sun
    const currentDay = now.getDay(); // 0 (Sun) to 6 (Sat)
    const distanceToMonday = currentDay === 0 ? -6 : 1 - currentDay;
    const mondayOfWeek = new Date(now);
    mondayOfWeek.setDate(now.getDate() + distanceToMonday);
    mondayOfWeek.setHours(0,0,0,0);

    const weeklyData = [0, 0, 0, 0, 0, 0, 0];
    const monthlyRevenueMap = {};

    appointments.forEach((app) => {
      const appDate = app.date; // YYYY-MM-DD
      const paid = parseFloat(app.payment) || 0;
      const due = parseFloat(app.pendingPayment) || 0;
      const status = app.status || "Pending";
      const proc = normalizeProcedureName(app.procedure);
      
      const patientId = app.phone || app.name || "Unknown";
      if (!patientAppointmentsMap[patientId]) {
        patientAppointmentsMap[patientId] = [];
      }
      patientAppointmentsMap[patientId].push(app);

      if (appDate === todayStr) {
        todayCount++;
        todayRevenue += paid;
        newPatientsToday++;
        todayList.push(app);
      } else if (appDate === yesterdayStr) {
        yesterdayCount++;
        yesterdayRevenue += paid;
      }

      if (appDate && appDate.startsWith(currentMonthStr)) {
        monthlyCount++;
        currentMonthRevenue += paid;
        currentMonthPatients.add(patientId);
      }

      if (status === "Pending") {
        pendingConfirmations++;
        if (appDate && new Date(appDate) < new Date(todayStr)) {
          noShowCount++;
        }
      }

      if (status === "Canceled") {
        canceledCount++;
      }

      procedureCounts[proc] = (procedureCounts[proc] || 0) + 1;

      if (due > 0) {
        totalOutstanding += due;
        uniqueDuesPatients.add(patientId);
        if (appDate && new Date(appDate) < thirtyDaysAgo) {
          overdue30DaysCount++;
        }
      }

      if (appDate) {
        const dateObj = new Date(appDate);
        dateObj.setHours(0,0,0,0);
        const diffTime = dateObj - mondayOfWeek;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays >= 0 && diffDays < 7) {
          weeklyData[diffDays]++;
        }
      }

      if (appDate) {
        const monthKey = appDate.slice(0, 7);
        monthlyRevenueMap[monthKey] = (monthlyRevenueMap[monthKey] || 0) + paid;
      }
    });

    todayList.sort((a, b) => a.time.localeCompare(b.time));

    const totalPatientsCount = Object.keys(patientAppointmentsMap).length;
    const activePatients = new Set();
    Object.entries(patientAppointmentsMap).forEach(([pid, list]) => {
      const hasActive = list.some(a => a.status === "Confirmed" || a.status === "Done");
      if (hasActive) activePatients.add(pid);
    });
    const activeCount = activePatients.size;
    const inactiveCount = Math.max(0, totalPatientsCount - activeCount);

    let returnCount = 0;
    Object.values(patientAppointmentsMap).forEach((list) => {
      if (list.length > 1) returnCount++;
    });
    const returnRate = totalPatientsCount > 0 ? Math.round((returnCount / totalPatientsCount) * 100) : 0;

    const totalApps = appointments.length;
    const cancelRate = totalApps > 0 ? Math.round((canceledCount / totalApps) * 100) : 0;
    const noShowRate = totalApps > 0 ? Math.round((noShowCount / totalApps) * 100) : 0;

    const hoursBooked = Math.min(todayCount, 8);
    const chairUtilPercent = Math.round((hoursBooked / 8) * 100);

    const getProcedureColor = (name) => {
      switch (name) {
        case "Teeth Cleaning":
        case "Cleaning":
          return "#2a78d6";
        case "Consultation":
          return "#1baf7a";
        case "Tooth Filling":
        case "Filling":
          return "#6a00ff";
        case "Root Canal":
        case "Root canal":
          return "#eb6834";
        case "Extraction":
          return "#f1c40f";
        case "Braces Check":
        case "Braces":
          return "#e91e63";
        case "Teeth Whitening":
          return "#00cfd5";
        case "Problem / Pain":
          return "#e74c3c";
        default:
          return "#898781";
      }
    };

    const sortedProcedures = Object.entries(procedureCounts)
      .map(([name, count]) => ({
        name,
        count,
        color: getProcedureColor(name)
      }))
      .sort((a, b) => b.count - a.count);

    const monthlyLabels = [];
    const monthlyRevenueData = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const k = d.toISOString().slice(0, 7);
      const label = d.toLocaleDateString("en-IN", { month: "short" });
      monthlyLabels.push(label);
      monthlyRevenueData.push(monthlyRevenueMap[k] || 0);
    }

    return {
      todayCount,
      yesterdayCount,
      todayRevenue,
      yesterdayRevenue,
      newPatientsToday,
      monthlyCount,
      pendingConfirmations,
      todayList,
      totalPatientsCount,
      activeCount,
      inactiveCount,
      returnRate,
      cancelRate,
      noShowRate,
      chairUtilPercent,
      hoursBooked,
      weeklyData,
      monthlyLabels,
      monthlyRevenueData,
      sortedProcedures,
      totalOutstanding,
      duesPatientsCount: uniqueDuesPatients.size,
      overdue30DaysCount,
      currentMonthRevenue,
      currentMonthNewPatients: currentMonthPatients.size
    };
  };

  const statsObj = getOverviewStats();

  // Initialize Chart.js when Overview Dashboard is loaded
  useEffect(() => {
    if (activeTab !== "dashboard" || loading) return;

    const weeklyCtx = document.getElementById("weeklyAppointmentsChart");
    const monthlyCtx = document.getElementById("monthlyRevenueChart");

    if (weeklyCtx && window.Chart) {
      if (window.weeklyChartRef) {
        window.weeklyChartRef.destroy();
      }

      window.weeklyChartRef = new window.Chart(weeklyCtx, {
        type: "bar",
        data: {
          labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
          datasets: [{
            data: statsObj.weeklyData,
            backgroundColor: "#2a78d6",
            borderRadius: 4,
            borderSkipped: false,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false }
          },
          scales: {
            x: {
              grid: {
                color: getComputedStyle(document.documentElement).getPropertyValue("--gridline-color").trim() || "#e1e0d9"
              },
              ticks: {
                color: "#898781",
                font: { size: 11 }
              }
            },
            y: {
              beginAtZero: true,
              grid: {
                color: getComputedStyle(document.documentElement).getPropertyValue("--gridline-color").trim() || "#e1e0d9"
              },
              ticks: {
                color: "#898781",
                font: { size: 11 }
              }
            }
          }
        }
      });
    }

    if (monthlyCtx && window.Chart) {
      if (window.monthlyChartRef) {
        window.monthlyChartRef.destroy();
      }

      window.monthlyChartRef = new window.Chart(monthlyCtx, {
        type: "line",
        data: {
          labels: statsObj.monthlyLabels,
          datasets: [{
            data: statsObj.monthlyRevenueData,
            borderColor: "#1baf7a",
            backgroundColor: "rgba(27, 175, 122, 0.08)",
            fill: true,
            tension: 0.3,
            borderWidth: 2,
            pointRadius: 2,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false }
          },
          scales: {
            x: {
              grid: {
                color: getComputedStyle(document.documentElement).getPropertyValue("--gridline-color").trim() || "#e1e0d9"
              },
              ticks: {
                color: "#898781",
                font: { size: 11 }
              }
            },
            y: {
              grid: {
                color: getComputedStyle(document.documentElement).getPropertyValue("--gridline-color").trim() || "#e1e0d9"
              },
              ticks: {
                color: "#898781",
                font: { size: 11 },
                callback: function(value) {
                  return "₹" + (value / 1000) + "k";
                }
              }
            }
          }
        }
      });
    }

    return () => {
      if (window.weeklyChartRef) {
        window.weeklyChartRef.destroy();
        window.weeklyChartRef = null;
      }
      if (window.monthlyChartRef) {
        window.monthlyChartRef.destroy();
        window.monthlyChartRef = null;
      }
    };
  }, [activeTab, loading, appointments]);

  // Sync Chart Gridline colors when switching Light/Dark themes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const color = getComputedStyle(document.documentElement).getPropertyValue("--gridline-color").trim() || "#e1e0d9";
      if (window.weeklyChartRef) {
        window.weeklyChartRef.options.scales.x.grid.color = color;
        window.weeklyChartRef.options.scales.y.grid.color = color;
        window.weeklyChartRef.update();
      }
      if (window.monthlyChartRef) {
        window.monthlyChartRef.options.scales.x.grid.color = color;
        window.monthlyChartRef.options.scales.y.grid.color = color;
        window.monthlyChartRef.update();
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  // Initialize Income vs Expenditures comparison chart
  useEffect(() => {
    if (activeTab !== "accounts" || accountsSubTab !== "monthly" || loading) return;

    const compCtx = document.getElementById("incomeVsExpenditureChart");
    if (compCtx && window.Chart) {
      if (window.comparisonChartRef) {
        window.comparisonChartRef.destroy();
      }

      const chartData = getComparisonChartData();

      window.comparisonChartRef = new window.Chart(compCtx, {
        type: "bar",
        data: {
          labels: chartData.labels,
          datasets: [
            {
              label: "Patient Income",
              data: chartData.incomeData,
              backgroundColor: "#1baf7a",
              borderRadius: 4,
            },
            {
              label: "Expenditures",
              data: chartData.expenditureData,
              backgroundColor: "#eb6834",
              borderRadius: 4,
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { 
              display: true,
              position: "top",
              labels: {
                color: "#898781",
                boxWidth: 12,
                font: { size: 11 }
              }
            }
          },
          scales: {
            x: {
              grid: {
                color: getComputedStyle(document.documentElement).getPropertyValue("--gridline-color").trim() || "#e1e0d9"
              },
              ticks: {
                color: "#898781",
                font: { size: 11 }
              }
            },
            y: {
              beginAtZero: true,
              grid: {
                color: getComputedStyle(document.documentElement).getPropertyValue("--gridline-color").trim() || "#e1e0d9"
              },
              ticks: {
                color: "#898781",
                font: { size: 11 },
                callback: function(value) {
                  return "₹" + (value / 1000) + "k";
                }
              }
            }
          }
        }
      });
    }

    return () => {
      if (window.comparisonChartRef) {
        window.comparisonChartRef.destroy();
        window.comparisonChartRef = null;
      }
    };
  }, [activeTab, accountsSubTab, loading, appointments, expenses]);

  // Delete handlers
  const handleDeleteAppointment = (id) => {
    if (!window.confirm("Delete this appointment record?")) return;
    remove(ref(db, `appointments/${id}`));
  };

  const handleDeleteCareer = (id) => {
    if (!window.confirm("Delete this job application?")) return;
    remove(ref(db, `careers/${id}`));
  };

  const handleUpdateStatus = (id, newStatus) => {
    update(ref(db, `appointments/${id}`), { status: newStatus });
  };

  const handleUpdatePayments = (id, paidAmount, pendingAmount) => {
    update(ref(db, `appointments/${id}`), { 
      payment: paidAmount,
      pendingPayment: pendingAmount 
    })
      .then(() => {
        alert("Financial details saved successfully!");
      })
      .catch((err) => {
        alert("Error saving: " + err.message);
      });
  };



  const handleUpdateProcedure = (id, procedure) => {
    update(ref(db, `appointments/${id}`), { 
      procedure 
    })
      .then(() => {
        alert("Procedure updated successfully!");
      })
      .catch((err) => {
        alert("Error saving: " + err.message);
      });
  };

  // Safe formatting helper to avoid Safari/iOS timezone RangeError crashes
  const formatDateSafe = (dateStr) => {
    if (!dateStr) return "-";
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      const year = parts[0];
      const month = parts[1];
      const day = parts[2];
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const mIdx = parseInt(month, 10) - 1;
      if (mIdx >= 0 && mIdx < 12) {
        return `${day} ${months[mIdx]} ${year}`;
      }
    }
    return dateStr;
  };

  // Expenses calculations and handlers
  const getExpensesTotals = () => {
    let total = 0;
    let marketing = 0;
    let equipments = 0;
    let other = 0;

    expenses.forEach((item) => {
      const amt = parseFloat(item.amount) || 0;
      total += amt;
      const cat = item.category || "";
      if (cat === "Marketing") {
        marketing += amt;
      } else if (cat === "Clinical supplies" || cat === "Equipment" || cat === "Facility") {
        equipments += amt;
      } else {
        other += amt;
      }
    });

    return { total, marketing, equipments, other };
  };

  const expensesTotals = getExpensesTotals();

  const getAccountsMonthlyStats = () => {
    const now = new Date();
    const currentMonthStr = now.toISOString().slice(0, 7); // "YYYY-MM"

    let totalSpent = 0;
    let totalEntries = 0;

    let suppliesSpent = 0;
    let suppliesEntries = 0;

    let marketingSpent = 0;
    let marketingEntries = 0;

    let facilitySpent = 0;
    let facilityEntries = 0;

    expenses.forEach((item) => {
      const amt = parseFloat(item.amount) || 0;
      const dateStr = item.date || "";
      const cat = item.category || "";

      if (dateStr.startsWith(currentMonthStr)) {
        totalSpent += amt;
        totalEntries++;

        if (cat === "Clinical supplies") {
          suppliesSpent += amt;
          suppliesEntries++;
        } else if (cat === "Marketing") {
          marketingSpent += amt;
          marketingEntries++;
        } else if (cat === "Facility" || cat === "Equipment") {
          facilitySpent += amt;
          facilityEntries++;
        }
      }
    });

    return {
      totalSpent,
      totalEntries,
      suppliesSpent,
      suppliesEntries,
      marketingSpent,
      marketingEntries,
      facilitySpent,
      facilityEntries,
    };
  };

  const monthlyAccountsStats = getAccountsMonthlyStats();

  const getIncomeStats = () => {
    const now = new Date();
    const currentMonthStr = now.toISOString().slice(0, 7); // "YYYY-MM"

    let totalIncome = 0;
    let monthIncome = 0;
    let totalDues = 0;
    const incomeTransactions = [];

    appointments.forEach((app) => {
      const paid = parseFloat(app.payment) || 0;
      const due = parseFloat(app.pendingPayment) || 0;
      const appDate = app.date || "";

      totalIncome += paid;
      totalDues += due;

      if (appDate.startsWith(currentMonthStr)) {
        monthIncome += paid;
      }

      if (paid > 0 || due > 0) {
        incomeTransactions.push({
          id: app.id,
          name: app.name,
          date: appDate,
          procedure: app.procedure || "Consultation",
          paid,
          due,
        });
      }
    });

    incomeTransactions.sort((a, b) => (b.date || "").localeCompare(a.date || ""));

    return { totalIncome, monthIncome, totalDues, incomeTransactions };
  };

  const incomeStats = getIncomeStats();

  const getComparisonChartData = () => {
    const now = new Date();
    const monthsData = [];

    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const monthKey = `${year}-${month}`;
      const label = d.toLocaleDateString("en-IN", { month: "short" });
      monthsData.push({
        label,
        monthKey,
        income: 0,
        expenditure: 0,
      });
    }

    appointments.forEach((app) => {
      const paid = parseFloat(app.payment) || 0;
      const appDate = app.date || "";
      if (appDate) {
        const monthKey = appDate.slice(0, 7);
        const match = monthsData.find((m) => m.monthKey === monthKey);
        if (match) match.income += paid;
      }
    });

    expenses.forEach((item) => {
      const amt = parseFloat(item.amount) || 0;
      const expDate = item.date || "";
      if (expDate) {
        const monthKey = expDate.slice(0, 7);
        const match = monthsData.find((m) => m.monthKey === monthKey);
        if (match) match.expenditure += amt;
      }
    });

    return {
      labels: monthsData.map((m) => m.label),
      incomeData: monthsData.map((m) => m.income),
      expenditureData: monthsData.map((m) => m.expenditure),
    };
  };

  const getMonthlyGroupedSummary = () => {
    const summary = {};

    appointments.forEach((app) => {
      const paid = parseFloat(app.payment) || 0;
      const dateStr = app.date || "";
      if (dateStr) {
        const monthKey = dateStr.slice(0, 7);
        if (!summary[monthKey]) {
          summary[monthKey] = { income: 0, expenditure: 0 };
        }
        summary[monthKey].income += paid;
      }
    });

    expenses.forEach((item) => {
      const amt = parseFloat(item.amount) || 0;
      const dateStr = item.date || "";
      if (dateStr) {
        const monthKey = dateStr.slice(0, 7);
        if (!summary[monthKey]) {
          summary[monthKey] = { income: 0, expenditure: 0 };
        }
        summary[monthKey].expenditure += amt;
      }
    });

    return summary;
  };

  const monthlyGroupedSummary = getMonthlyGroupedSummary();

  const handleExpenseSubmit = (e) => {
    e.preventDefault();
    if (!newExpense.amount || !newExpense.date) return;

    const subCatValue = newExpense.subCategory === "Other (Write-in)"
      ? (customSubCategory.trim() || "Other")
      : newExpense.subCategory;

    push(ref(db, "expenses"), {
      ...newExpense,
      subCategory: subCatValue,
      createdAt: new Date().toISOString(),
    })
      .then(() => {
        alert("Expenditure recorded successfully!");
        setNewExpense({
          date: new Date().toISOString().slice(0, 10),
          category: "Clinical supplies",
          subCategory: "Gloves, masks, syringes, cotton rolls",
          amount: "",
          paymentMethod: "Cash",
          description: "",
          vendor: "",
          notes: "",
          reference: "",
        });
        setCustomSubCategory("");
      })
      .catch((err) => {
        alert("Error recording expense: " + err.message);
      });
  };

  const handleDeleteExpense = (id) => {
    if (!window.confirm("Delete this expense transaction?")) return;
    remove(ref(db, `expenses/${id}`));
  };

  const downloadExpensesCSV = () => {
    const header = ["Date", "Category", "Description", "Amount"];
    const rows = expenses.map((item) => [
      item.date,
      item.category,
      item.description || "",
      item.amount,
    ]);

    const csvContent = "data:text/csv;charset=utf-8,"
      + [header, ...rows].map((e) => e.join(",")).join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csvContent);
    link.download = "expenditures_report.csv";
    link.click();
  };

  const handleAddPatientSubmit = (e) => {
    e.preventDefault();
    if (!newPatient.name || !newPatient.phone || !newPatient.date || !newPatient.time) {
      alert("Please fill in Name, Phone, Date, and Time.");
      return;
    }

    push(ref(db, "appointments"), {
      ...newPatient,
      createdAt: new Date().toISOString(),
    })
      .then(() => {
        alert("Patient record added successfully!");
        setNewPatient({
          name: "",
          email: "",
          phone: "",
          date: "",
          time: "",
          notes: "",
          procedure: "Consultation",
          status: "Pending",
          payment: "0",
          pendingPayment: "0",
        });
        setActiveTab("appointments");
      })
      .catch((err) => {
        console.error("Error adding patient record:", err);
        alert("Error adding record: " + err.message);
      });
  };

  const subCategoryOptions = {
    "Clinical supplies": [
      "Gloves, masks, syringes, cotton rolls",
      "Dental materials (composite, cement, impression)",
      "Sterilisation supplies",
      "Medicines and anaesthetics",
      "Other (Write-in)"
    ],
    "Equipment": [
      "Dental chair maintenance",
      "X-ray machine servicing",
      "Handpiece repair",
      "New equipment purchase",
      "Computer and tech",
      "Other (Write-in)"
    ],
    "Staff and HR": [
      "Salaries",
      "Bonus and incentives",
      "Staff training",
      "Uniform",
      "Other (Write-in)"
    ],
    "Facility": [
      "Rent",
      "Electricity and water",
      "Internet and phone",
      "Housekeeping",
      "Repairs and maintenance",
      "Other (Write-in)"
    ],
    "Marketing": [
      "Social media ads",
      "Photography and videography",
      "Pamphlets and printing",
      "Website maintenance",
      "Google ads",
      "Other (Write-in)"
    ],
    "Miscellaneous": [
      "Refreshments for patients",
      "Stationery",
      "Bank charges",
      "Other (Write-in)"
    ]
  };

  const getCategoryBadgeDetails = (cat) => {
    switch (cat) {
      case "Staff and HR":
        return { label: "Staff", className: "badge-green" };
      case "Clinical supplies":
        return { label: "Clinical", className: "badge-blue" };
      case "Marketing":
        return { label: "Marketing", className: "badge-pink" };
      case "Equipment":
        return { label: "Equipment", className: "badge-purple" };
      case "Facility":
        return { label: "Facility", className: "badge-orange" };
      default:
        return { label: "Misc", className: "badge-grey" };
    }
  };

  // Download CSV
  const downloadCSV = () => {
    let header = [];
    let rows = [];
    let filename = "";

    if (activeTab === "appointments") {
      header = ["S.No", "Name", "Email", "Phone", "Date", "Time", "Notes", "Status", "Paid Amount", "Pending Amount", "Created At"];
      rows = appointments.map((item, index) => [
        index + 1,
        item.name,
        item.email,
        item.phone,
        item.date,
        item.time,
        item.notes || "",
        item.status || "Pending",
        item.payment || "0",
        item.pendingPayment || "0",
        item.createdAt,
      ]);
      filename = "appointments.csv";
    } else {
      header = ["S.No", "Name", "Email", "Phone", "Role", "Resume Link", "Submitted At"];
      rows = applications.map((item, index) => [
        index + 1,
        item.name,
        item.email,
        item.phone,
        item.role,
        item.resumeLink,
        new Date(item.createdAt).toLocaleString("en-IN"),
      ]);
      filename = "job_applications.csv";
    }

    let csvContent = "data:text/csv;charset=utf-8,"
      + [header, ...rows].map((e) => e.join(",")).join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csvContent);
    link.download = filename;
    link.click();
  };

  // Avatar helper styling
  const getAvatarStyle = (name) => {
    const ramps = [
      { bg: "rgba(42, 120, 214, 0.12)", text: "#2a78d6" }, // Blue
      { bg: "rgba(27, 175, 122, 0.12)", text: "#1baf7a" }, // Green
      { bg: "rgba(106, 0, 255, 0.12)", text: "#6a00ff" },  // Purple
      { bg: "rgba(235, 104, 52, 0.12)", text: "#eb6834" }, // Coral
      { bg: "rgba(241, 196, 15, 0.12)", text: "#f1c40f" }, // Amber
      { bg: "rgba(233, 30, 99, 0.12)", text: "#e91e63" },  // Pink
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const idx = Math.abs(hash) % ramps.length;
    return ramps[idx];
  };

  const getInitials = (name) => {
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  // Data mocks for dashboard components
  const todayAppointments = [
    { time: "09:00 AM", name: "Amit Patel", procedure: "Teeth Cleaning", status: "Done" },
    { time: "10:30 AM", name: "Rina Sen", procedure: "Consultation", status: "Confirmed" },
    { time: "11:45 AM", name: "Karan Das", procedure: "Root Canal", status: "Confirmed" },
    { time: "02:00 PM", name: "Priya Baruah", procedure: "Tooth Filling", status: "Pending" },
    { time: "04:30 PM", name: "Suresh Mehta", procedure: "Extraction", status: "Confirmed" },
    { time: "06:00 PM", name: "Ananya Dutta", procedure: "Braces Check", status: "Pending" },
  ];

  const procedures = [
    { name: "Cleaning", count: 42, color: "#2a78d6" },
    { name: "Consultation", count: 35, color: "#1baf7a" },
    { name: "Filling", count: 27, color: "#6a00ff" },
    { name: "Root canal", count: 18, color: "#eb6834" },
    { name: "Extraction", count: 14, color: "#f1c40f" },
    { name: "Braces", count: 9, color: "#e91e63" },
  ];

  const formattedDate = new Date().toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return (
    <div className="admin-dashboard-container">
      {/* LEFT SIDE PANEL (SIDEBAR) */}
      <aside className="admin-sidebar">
        <div className="sidebar-brand">
          <h2>PrimeMax Admin</h2>
        </div>
        <nav className="sidebar-nav">
          <button
            className={`nav-item ${activeTab === "dashboard" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("dashboard");
              setSearchTerm("");
            }}
          >
            <i className="ti ti-dashboard"></i> Overview
          </button>
          <button
            className={`nav-item ${activeTab === "appointments" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("appointments");
              setSearchTerm("");
            }}
          >
            <i className="ti ti-calendar"></i> Appointments List
          </button>
          <button
            className={`nav-item ${activeTab === "careers" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("careers");
              setSearchTerm("");
            }}
          >
            <i className="ti ti-briefcase"></i> Job Applications
          </button>
          <button
            className={`nav-item ${activeTab === "add-record" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("add-record");
              setSearchTerm("");
            }}
          >
            <i className="ti ti-plus"></i> Add Patient Record
          </button>
          <button
            className={`nav-item ${activeTab === "accounts" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("accounts");
              setSearchTerm("");
            }}
          >
            <i className="ti ti-cash"></i> Clinic Accounts
          </button>
          <button className="nav-item logout-btn" onClick={handleLogout}>
            <i className="ti ti-logout"></i> Logout
          </button>
        </nav>
      </aside>

      {/* RIGHT CONTENT PANEL */}
      <main className="admin-main-content">
        {loading ? (
          <p className="loading-text">Loading records...</p>
        ) : activeTab === "dashboard" ? (
          /* ======================================================
             SECTION 1 — OVERVIEW DASHBOARD VIEW
          ====================================================== */
          <div className="dashboard-overview-wrapper">
            {/* Top Bar */}
            <div className="dashboard-topbar">
              <h1 className="topbar-heading">Admin dashboard</h1>
              <div className="topbar-pills">
                <span className="pill-date">{formattedDate}</span>
                <span className="pill-status">Clinic open</span>
              </div>
            </div>

            {/* Section 2 — Metric Cards (4 Column Grid) */}
            <div className="metric-cards-grid">
              <div className="metric-card">
                <div className="metric-header">
                  <i className="ti ti-calendar-event blue-icon"></i>
                  <span>Today's appointments</span>
                </div>
                <div className="metric-value">{statsObj.todayCount}</div>
                <div className="metric-subtext">
                  <span className={statsObj.todayCount >= statsObj.yesterdayCount ? "trend-up" : "trend-down"}>
                    <i className={statsObj.todayCount >= statsObj.yesterdayCount ? "ti ti-arrow-up-right" : "ti ti-arrow-down-right"}></i>
                    {statsObj.todayCount >= statsObj.yesterdayCount ? ` +${statsObj.todayCount - statsObj.yesterdayCount}` : ` ${statsObj.todayCount - statsObj.yesterdayCount}`}
                  </span> vs yesterday
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-header">
                  <i className="ti ti-currency-rupee green-icon"></i>
                  <span>Revenue today</span>
                </div>
                <div className="metric-value">₹{statsObj.todayRevenue.toLocaleString("en-IN")}</div>
                <div className="metric-subtext">
                  <span className={statsObj.todayRevenue >= statsObj.yesterdayRevenue ? "trend-up" : "trend-down"}>
                    <i className={statsObj.todayRevenue >= statsObj.yesterdayRevenue ? "ti ti-arrow-up-right" : "ti ti-arrow-down-right"}></i>
                    {statsObj.yesterdayRevenue > 0
                      ? ` ${Math.round(((statsObj.todayRevenue - statsObj.yesterdayRevenue) / statsObj.yesterdayRevenue) * 100)}%`
                      : " +0%"}
                  </span> vs yesterday
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-header">
                  <i className="ti ti-users blue-icon"></i>
                  <span>New patients today</span>
                </div>
                <div className="metric-value">{statsObj.newPatientsToday}</div>
                <div className="metric-subtext">Monthly total: {statsObj.monthlyCount}</div>
              </div>

              <div className="metric-card">
                <div className="metric-header">
                  <i className="ti ti-alert-circle orange-icon"></i>
                  <span>Pending confirmations</span>
                </div>
                <div className="metric-value">{statsObj.pendingConfirmations}</div>
                <div className="metric-subtext">
                  {statsObj.pendingConfirmations > 0 ? (
                    <span className="badge-attention">Needs attention</span>
                  ) : (
                    <span>All clear</span>
                  )}
                </div>
              </div>
            </div>

            {/* Section 3 — Main Grid (2fr + 1fr columns) */}
            <div className="main-layout-grid">
              {/* Left Column — Today's Appointments */}
              <div className="grid-card left-main-card">
                <h3 className="card-title">Today's appointments</h3>
                <div className="today-appointments-list">
                  {statsObj.todayList.length === 0 ? (
                    <p style={{ fontSize: "13px", color: "var(--text-secondary)", margin: 0, padding: "12px 0" }}>
                      No appointments scheduled for today.
                    </p>
                  ) : (
                    statsObj.todayList.map((patient, idx) => {
                      const avatarStyle = getAvatarStyle(patient.name);
                      const initials = getInitials(patient.name);
                      return (
                        <div className="patient-appointment-row" key={idx}>
                          <div className="patient-time">{patient.time}</div>
                          <div 
                            className="patient-avatar" 
                            style={{ backgroundColor: avatarStyle.bg, color: avatarStyle.text }}
                          >
                            {initials}
                          </div>
                          <div className="patient-info">
                            <div className="patient-name">{patient.name}</div>
                            <div className="patient-procedure">{patient.procedure || "Consultation"}</div>
                          </div>
                          <div className="patient-status">
                            <span className={`status-badge badge-${(patient.status || "Pending").toLowerCase()}`}>
                              {patient.status || "Pending"}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Right Column — Stacked Cards */}
              <div className="right-sidebar-column">
                {/* Clinic Stats Card */}
                <div className="grid-card">
                  <h3 className="card-title">Clinic stats</h3>
                  <div className="stats-list-rows">
                    <div className="stat-row">
                      <span className="stat-label">
                        <i className="ti ti-users-group"></i> Total patients
                      </span>
                      <span className="stat-value">{statsObj.totalPatientsCount}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">
                        <i className="ti ti-user-check"></i> Active patients
                      </span>
                      <span className="stat-value">{statsObj.activeCount}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">
                        <i className="ti ti-refresh"></i> Return rate
                      </span>
                      <span className="stat-value text-green">{statsObj.returnRate}%</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">
                        <i className="ti ti-calendar-cancel"></i> Cancel rate
                      </span>
                      <span className="stat-value text-orange">{statsObj.cancelRate}%</span>
                    </div>
                  </div>
                </div>


              </div>
            </div>

            {/* Section 4 — Two Equal Column Chart Grid */}
            <div className="two-column-grid">
              <div className="grid-card">
                <h3 className="card-title">Appointments this week</h3>
                <div className="chart-wrapper">
                  <canvas id="weeklyAppointmentsChart"></canvas>
                </div>
              </div>

              <div className="grid-card">
                <h3 className="card-title">Revenue last 6 months</h3>
                <div className="chart-wrapper">
                  <canvas id="monthlyRevenueChart"></canvas>
                </div>
              </div>
            </div>

            {/* Section 5 — Two Equal Column Procedures & Payments Grid */}
            <div className="two-column-grid">
              {/* Top Procedures */}
              <div className="grid-card">
                <h3 className="card-title">Top procedures</h3>
                <div className="procedures-list">
                  {statsObj.sortedProcedures.map((proc, idx) => {
                    const maxVal = Math.max(...statsObj.sortedProcedures.map(p => p.count), 1);
                    const percent = (proc.count / maxVal) * 100;
                    return (
                      <div className="procedure-row" key={idx}>
                        <div className="procedure-row-text">
                          <span className="procedure-name">{proc.name}</span>
                          <span className="procedure-count">{proc.count}</span>
                        </div>
                        <div className="procedure-progress-track">
                          <div 
                            className="procedure-progress-bar" 
                            style={{ width: `${percent}%`, backgroundColor: proc.color }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Outstanding Payments */}
              <div className="grid-card">
                <h3 className="card-title">Outstanding payments</h3>
                <div className="payments-stats-list">
                  <div className="payment-stat-row">
                    <span>Total outstanding</span>
                    <span className="text-red font-bold">₹{statsObj.totalOutstanding.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="payment-stat-row">
                    <span>Patients with dues</span>
                    <span className="font-bold">{statsObj.duesPatientsCount}</span>
                  </div>
                  <div className="payment-stat-row">
                    <span>Overdue over 30 days</span>
                    <span className="text-orange font-bold">{statsObj.overdue30DaysCount}</span>
                  </div>
                </div>


              </div>
            </div>
          </div>
        ) : activeTab === "appointments" ? (
          /* ======================================================
             SECTION 2 — APPOINTMENTS MANAGER VIEW (BACKUP)
          ====================================================== */
          <div className="appointments-list-wrapper">
            <header className="content-header">
              <h1>Appointments</h1>
              <p>Total Records: {appointments.length}</p>
            </header>

            <div className="action-bar">
              <button className="csv-btn" onClick={downloadCSV}>
                ⬇ Download CSV
              </button>
            </div>

            <div className="filter-bar">
              <input
                type="text"
                className="search-input"
                placeholder="Search by name, phone or date..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
              />

              <div className="date-filters">
                <label>
                  From:
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </label>

                <label>
                  To:
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </label>
              </div>
            </div>

            {appointments.length === 0 ? (
              <p className="loading-text">No appointments found.</p>
            ) : (
              <div className="appointments-cards-grid">
                {appointments
                  .filter((item) => {
                    const term = searchTerm.toLowerCase();
                    const matchesSearch =
                      item.name.toLowerCase().includes(term) ||
                      item.phone.includes(term) ||
                      item.date.includes(term);

                    const afterStart = startDate ? new Date(item.date) >= new Date(startDate) : true;
                    const beforeEnd = endDate ? new Date(item.date) <= new Date(endDate) : true;

                    return matchesSearch && afterStart && beforeEnd;
                  })
                  .map((item, index) => {
                    const avatarStyle = getAvatarStyle(item.name);
                    const initials = getInitials(item.name);
                    return (
                      <div className="appointment-detail-card" key={item.id}>
                        {/* Top Card Row */}
                        <div className="card-top-row">
                          <span className="card-index-pill">Record #{index + 1}</span>
                          <button
                            className="delete-record-btn"
                            onClick={() => handleDeleteAppointment(item.id)}
                          >
                            <i className="ti ti-trash"></i> Delete Record
                          </button>
                        </div>

                        {/* Mid Section */}
                        <div className="card-middle-content">
                          {/* Profile */}
                          <div className="patient-profile-section">
                            <div 
                              className="patient-profile-avatar"
                              style={{ backgroundColor: avatarStyle.bg, color: avatarStyle.text }}
                            >
                              {initials}
                            </div>
                            <div className="patient-profile-details">
                              <h4 className="patient-profile-name">{item.name}</h4>
                              <div className="patient-contact-item">
                                <i className="ti ti-mail"></i> {item.email}
                              </div>
                              <div className="patient-contact-item">
                                <i className="ti ti-phone"></i> {item.phone}
                              </div>
                              {item.procedure && (
                                <div className="patient-contact-item patient-procedure-tag">
                                  <i className="ti ti-stethoscope"></i> {item.procedure}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Time details */}
                          <div className="appointment-time-section">
                            <div className="time-badge">
                              <i className="ti ti-calendar"></i> {item.date}
                            </div>
                            <div className="time-badge">
                              <i className="ti ti-clock"></i> {item.time}
                            </div>
                          </div>

                          {/* Status Badge & Actions */}
                          <div className="appointment-status-section">
                            <span className="section-subtitle">Status</span>
                            <span className={`status-badge-list status-${(item.status || "Pending").toLowerCase()}`}>
                              {item.status || "Pending"}
                            </span>
                            <div className="status-buttons">
                              <button
                                className="status-btn btn-confirm"
                                title="Confirmed"
                                onClick={() => handleUpdateStatus(item.id, "Confirmed")}
                              >
                                ✔
                              </button>
                              <button
                                className="status-btn btn-cancel"
                                title="Canceled"
                                onClick={() => handleUpdateStatus(item.id, "Canceled")}
                              >
                                ✖
                              </button>
                              <button
                                className="status-btn btn-pending"
                                title="Pending"
                                onClick={() => handleUpdateStatus(item.id, "Pending")}
                              >
                                ⌛
                              </button>
                            </div>
                          </div>

                          {/* Financials widget */}
                          <div className="appointment-payment-section">
                            <span className="section-subtitle">Financials</span>
                            <div className="payment-fields-wrapper">
                              <div className="payment-input-group">
                                <span className="payment-field-label">Paid:</span>
                                <input
                                  type="number"
                                  className="payment-input"
                                  placeholder="₹ Paid"
                                  key={`paid-${item.payment || ""}`}
                                  defaultValue={item.payment || ""}
                                  id={`payment-input-${item.id}`}
                                />
                              </div>
                              <div className="payment-input-group" style={{ marginTop: "6px" }}>
                                <span className="payment-field-label">Due:</span>
                                <input
                                  type="number"
                                  className="payment-input"
                                  placeholder="₹ Due"
                                  key={`due-${item.pendingPayment || ""}`}
                                  defaultValue={item.pendingPayment || ""}
                                  id={`pending-payment-input-${item.id}`}
                                />
                              </div>
                              <button
                                className="save-payment-btn"
                                style={{ marginTop: "8px", width: "100%" }}
                                onClick={() => {
                                  const paidVal = document.getElementById(`payment-input-${item.id}`).value;
                                  const dueVal = document.getElementById(`pending-payment-input-${item.id}`).value;
                                  handleUpdatePayments(item.id, paidVal, dueVal);
                                }}
                              >
                                Save Financials
                              </button>
                            </div>
                          </div>



                          {/* Actual Procedure widget */}
                          <div className="appointment-payment-section">
                            <span className="section-subtitle">Procedure Status</span>
                            <div className="payment-fields-wrapper">
                              <select
                                className="payment-input"
                                style={{ width: "100%" }}
                                key={`proc-${item.procedure || "Consultation"}`}
                                defaultValue={item.procedure || "Consultation"}
                                id={`procedure-select-${item.id}`}
                              >
                                <option value="Consultation">Consultation</option>
                                <option value="Problem / Pain">Problem / Pain</option>
                                <option value="Teeth Whitening">Teeth Whitening</option>
                                <option value="Teeth Cleaning">Teeth Cleaning</option>
                                <option value="Tooth Filling">Tooth Filling</option>
                                <option value="Root Canal">Root Canal</option>
                                <option value="Extraction">Extraction</option>
                                <option value="Braces Check">Braces Check</option>
                                <option value="Other">Other</option>
                              </select>
                              <button
                                className="save-payment-btn"
                                style={{ marginTop: "8px", width: "100%" }}
                                onClick={() => {
                                  const procVal = document.getElementById(`procedure-select-${item.id}`).value;
                                  handleUpdateProcedure(item.id, procVal);
                                }}
                              >
                                Save Procedure
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Bottom Notes */}
                        {item.notes && (
                          <div className="card-notes-row">
                            <i className="ti ti-notes"></i>
                            <span className="notes-text"><strong>Notes: </strong>{item.notes}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        ) : activeTab === "careers" ? (
          /* ======================================================
             SECTION 3 — JOB APPLICATIONS VIEW
          ====================================================== */
          <div className="careers-list-wrapper">
            <header className="content-header">
              <h1>Job Applications</h1>
              <p>Total Records: {applications.length}</p>
            </header>

            <div className="action-bar">
              <button className="csv-btn" onClick={downloadCSV}>
                ⬇ Download CSV
              </button>
            </div>

            <div className="filter-bar">
              <input
                type="text"
                className="search-input"
                placeholder="Search by name, role, email or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
              />

              <div className="date-filters">
                <label>
                  From:
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </label>

                <label>
                  To:
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </label>
              </div>
            </div>

            {applications.length === 0 ? (
              <p className="loading-text">No job applications found.</p>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Role</th>
                    <th>Resume</th>
                    <th>Date Submitted</th>
                    <th>Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {applications
                    .filter((item) => {
                      const term = searchTerm.toLowerCase();
                      const matchesSearch =
                        item.name.toLowerCase().includes(term) ||
                        item.phone.includes(term) ||
                        item.email.toLowerCase().includes(term) ||
                        item.role.toLowerCase().includes(term);

                      const afterStart = startDate
                        ? new Date(item.createdAt) >= new Date(startDate + "T00:00:00")
                        : true;
                      const beforeEnd = endDate
                        ? new Date(item.createdAt) <= new Date(endDate + "T23:59:59")
                        : true;

                      return matchesSearch && afterStart && beforeEnd;
                    })
                    .map((item, index) => (
                      <tr key={item.id}>
                        <td>{index + 1}</td>
                        <td>{item.name}</td>
                        <td>{item.email}</td>
                        <td>{item.phone}</td>
                        <td>{item.role}</td>
                        <td>
                          <a
                            href={item.resumeLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="resume-link-btn"
                          >
                            View Resume ↗
                          </a>
                        </td>
                        <td>
                          {new Date(item.createdAt).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </td>
                        <td>
                          <button
                            className="delete-btn"
                            onClick={() => handleDeleteCareer(item.id)}
                          >
                            Delete Record
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        ) : activeTab === "add-record" ? (
          /* ======================================================
             SECTION 4 — ADD NEW PATIENT RECORD VIEW
          ====================================================== */
          <div className="add-patient-record-wrapper">
            <header className="content-header">
              <h1>Add Patient Record</h1>
              <p>Manually insert a new patient appointment into the database.</p>
            </header>

            <div className="grid-card add-patient-form-card">
              <form onSubmit={handleAddPatientSubmit} className="add-patient-form">
                <div className="form-grid-row">
                  <div className="form-group">
                    <label className="form-label">Full Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Rahul Sharma"
                      required
                      value={newPatient.name}
                      onChange={(e) => setNewPatient({ ...newPatient, name: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email Address</label>
                    <input
                      type="email"
                      className="form-control"
                      placeholder="e.g. name@example.com"
                      value={newPatient.email}
                      onChange={(e) => setNewPatient({ ...newPatient, email: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-grid-row">
                  <div className="form-group">
                    <label className="form-label">Phone Number *</label>
                    <input
                      type="tel"
                      className="form-control"
                      placeholder="e.g. +91 98765 43210"
                      required
                      value={newPatient.phone}
                      onChange={(e) => setNewPatient({ ...newPatient, phone: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Treatment / Procedure *</label>
                    <select
                      className="form-control"
                      value={newPatient.procedure}
                      onChange={(e) => setNewPatient({ ...newPatient, procedure: e.target.value })}
                    >
                      <option value="Consultation">Consultation</option>
                      <option value="Problem / Pain">Problem / Pain</option>
                      <option value="Teeth Whitening">Teeth Whitening</option>
                      <option value="Teeth Cleaning">Teeth Cleaning</option>
                      <option value="Tooth Filling">Tooth Filling</option>
                      <option value="Root Canal">Root Canal</option>
                      <option value="Extraction">Extraction</option>
                      <option value="Braces Check">Braces Check</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="form-grid-row">
                  <div className="form-group">
                    <label className="form-label">Date *</label>
                    <input
                      type="date"
                      className="form-control"
                      required
                      value={newPatient.date}
                      onChange={(e) => setNewPatient({ ...newPatient, date: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Time *</label>
                    <input
                      type="time"
                      className="form-control"
                      required
                      value={newPatient.time}
                      onChange={(e) => setNewPatient({ ...newPatient, time: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-grid-row">
                  <div className="form-group">
                    <label className="form-label">Paid Amount (₹)</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="e.g. 1500"
                      value={newPatient.payment}
                      onChange={(e) => setNewPatient({ ...newPatient, payment: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Due Amount (₹)</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="e.g. 500"
                      value={newPatient.pendingPayment}
                      onChange={(e) => setNewPatient({ ...newPatient, pendingPayment: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-grid-row">
                  <div className="form-group">
                    <label className="form-label">Initial Appointment Status</label>
                    <select
                      className="form-control"
                      value={newPatient.status}
                      onChange={(e) => setNewPatient({ ...newPatient, status: e.target.value })}
                    >
                      <option value="Pending">Pending</option>
                      <option value="Confirmed">Confirmed</option>
                      <option value="Canceled">Canceled</option>
                    </select>
                  </div>
                </div>

                <div className="form-group" style={{ gridColumn: "span 2", marginTop: "6px" }}>
                  <label className="form-label">Notes / Symptoms</label>
                  <textarea
                    className="form-control textarea-control"
                    placeholder="Describe any custom symptoms or guidelines..."
                    rows="3"
                    value={newPatient.notes}
                    onChange={(e) => setNewPatient({ ...newPatient, notes: e.target.value })}
                  />
                </div>

                <div className="form-submit-row" style={{ marginTop: "24px" }}>
                  <button type="submit" className="add-record-btn-submit">
                    ➕ Add Patient Record
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : (
          /* ======================================================
             SECTION 5 — ACCOUNTS & EXPENSES VIEW
          ====================================================== */
          <div className="accounts-wrapper">
            {/* Top bar header */}
            <div className="accounts-top-header">
              <h1 className="accounts-title">Clinic accounts</h1>
              <button className="csv-btn-outline" onClick={downloadExpensesCSV}>
                <i className="ti ti-download"></i> Export CSV
              </button>
            </div>

            {/* Inner sub-tabs selector pill row */}
            <div className="accounts-sub-tabs-pill-row">
              <button 
                className={`sub-tab-pill-btn ${accountsSubTab === "expenditure" ? "active" : ""}`}
                onClick={() => setAccountsSubTab("expenditure")}
              >
                Expenditure
              </button>
              <button 
                className={`sub-tab-pill-btn ${accountsSubTab === "monthly" ? "active" : ""}`}
                onClick={() => setAccountsSubTab("monthly")}
              >
                Monthly summary
              </button>
              <button 
                className={`sub-tab-pill-btn ${accountsSubTab === "income" ? "active" : ""}`}
                onClick={() => setAccountsSubTab("income")}
              >
                Income
              </button>
            </div>

            {/* 4-column metric grid of cards */}
            <div className="accounts-metric-cards-grid">
              <div className="accounts-metric-card">
                <div className="accounts-metric-header">
                  <i className="ti ti-arrows-up-down orange-icon"></i>
                  <span>Total spent this month</span>
                </div>
                <div className="accounts-metric-value">₹{monthlyAccountsStats.totalSpent.toLocaleString("en-IN")}</div>
                <div className="accounts-metric-subtext">{monthlyAccountsStats.totalEntries} entries</div>
              </div>

              <div className="accounts-metric-card">
                <div className="accounts-metric-header">
                  <i className="ti ti-bandage blue-icon"></i>
                  <span>Clinical supplies</span>
                </div>
                <div className="accounts-metric-value">₹{monthlyAccountsStats.suppliesSpent.toLocaleString("en-IN")}</div>
                <div className="accounts-metric-subtext">{monthlyAccountsStats.suppliesEntries} entries</div>
              </div>

              <div className="accounts-metric-card">
                <div className="accounts-metric-header">
                  <i className="ti ti-megaphone green-icon"></i>
                  <span>Marketing</span>
                </div>
                <div className="accounts-metric-value">₹{monthlyAccountsStats.marketingSpent.toLocaleString("en-IN")}</div>
                <div className="accounts-metric-subtext">{monthlyAccountsStats.marketingEntries} entries</div>
              </div>

              <div className="accounts-metric-card">
                <div className="accounts-metric-header">
                  <i className="ti ti-building purple-icon"></i>
                  <span>Facility</span>
                </div>
                <div className="accounts-metric-value">₹{monthlyAccountsStats.facilitySpent.toLocaleString("en-IN")}</div>
                <div className="accounts-metric-subtext">{monthlyAccountsStats.facilityEntries} entries</div>
              </div>
            </div>

            {/* Render views depending on active sub tab */}
            {accountsSubTab === "expenditure" && (
              <>
                {/* Split layout: Form (Left) & Log (Right) */}
                <div className="accounts-split-layout">
                  {/* Add Expenditure Form */}
                  <div className="grid-card account-form-card">
                    <h3 className="card-title">Add expenditure</h3>
                    <form onSubmit={handleExpenseSubmit} className="add-expense-form">
                      <div className="form-group">
                        <label className="form-label">Date</label>
                        <input
                          type="date"
                          className="form-control"
                          required
                          value={newExpense.date}
                          onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })}
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Category</label>
                        <select
                          className="form-control"
                          value={newExpense.category}
                          onChange={(e) => {
                            const cat = e.target.value;
                            setNewExpense({
                              ...newExpense,
                              category: cat,
                              subCategory: subCategoryOptions[cat][0]
                            });
                          }}
                        >
                          <option value="Clinical supplies">Clinical supplies</option>
                          <option value="Equipment">Equipment</option>
                          <option value="Staff and HR">Staff and HR</option>
                          <option value="Facility">Facility</option>
                          <option value="Marketing">Marketing</option>
                          <option value="Miscellaneous">Miscellaneous</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Sub-category</label>
                        <select
                          className="form-control"
                          value={newExpense.subCategory}
                          onChange={(e) => setNewExpense({ ...newExpense, subCategory: e.target.value })}
                        >
                          {subCategoryOptions[newExpense.category].map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      </div>

                      {newExpense.subCategory === "Other (Write-in)" && (
                        <div className="form-group">
                          <label className="form-label">Custom Sub-category *</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Type custom sub-category..."
                            required
                            value={customSubCategory}
                            onChange={(e) => setCustomSubCategory(e.target.value)}
                          />
                        </div>
                      )}

                      {/* Flex row for Amount & Payment method */}
                      <div className="form-flex-row">
                        <div className="form-group" style={{ flex: "2" }}>
                          <label className="form-label">Amount (₹)</label>
                          <input
                            type="number"
                            className="form-control"
                            required
                            placeholder="0"
                            value={newExpense.amount}
                            onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                          />
                        </div>
                        <div className="form-group" style={{ flex: "3" }}>
                          <label className="form-label">Payment method</label>
                          <select
                            className="form-control"
                            value={newExpense.paymentMethod}
                            onChange={(e) => setNewExpense({ ...newExpense, paymentMethod: e.target.value })}
                          >
                            <option value="Cash">Cash</option>
                            <option value="Card">Card</option>
                            <option value="UPI">UPI</option>
                            <option value="Bank Transfer">Bank Transfer</option>
                          </select>
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Description</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. Gloves — 10 boxes"
                          value={newExpense.description}
                          onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Paid to (vendor/person)</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. MedSupply Inc"
                          value={newExpense.vendor}
                          onChange={(e) => setNewExpense({ ...newExpense, vendor: e.target.value })}
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Notes (optional)</label>
                        <textarea
                          className="form-control textarea-control"
                          placeholder="Any additional context..."
                          rows="3"
                          value={newExpense.notes}
                          onChange={(e) => setNewExpense({ ...newExpense, notes: e.target.value })}
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Receipt reference (optional)</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. Ref: 19u28dn"
                          value={newExpense.reference}
                          onChange={(e) => setNewExpense({ ...newExpense, reference: e.target.value })}
                        />
                      </div>

                      <button type="submit" className="add-expense-btn-submit" style={{ marginTop: "8px", width: "100%", justifyContent: "center" }}>
                        ➕ Record Expenditure
                      </button>
                    </form>
                  </div>

                  {/* Expenditure Log Column */}
                  <div className="grid-card log-panel-card" style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    <div className="transaction-header-bar" style={{ marginBottom: "4px" }}>
                      <h3 className="card-title" style={{ margin: 0 }}>Expenditure log</h3>
                      <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
                        {
                          expenses.filter((item) => {
                            const term = expenseSearch.toLowerCase();
                            const desc = (item.description || "").toLowerCase();
                            const vend = (item.vendor || "").toLowerCase();
                            const matchesSearch = desc.includes(term) || vend.includes(term);
                            const matchesCategory = expenseFilterCategory === "All" || item.category === expenseFilterCategory;
                            const matchesMethod = expenseFilterMethod === "All" || item.paymentMethod === expenseFilterMethod;
                            return matchesSearch && matchesCategory && matchesMethod;
                          }).length
                        } entries
                      </span>
                    </div>

                    {/* Filter fields */}
                    <div className="log-filters-stack" style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search description or vendor..."
                        value={expenseSearch}
                        onChange={(e) => setExpenseSearch(e.target.value)}
                      />
                      <select
                        className="form-control"
                        value={expenseFilterCategory}
                        onChange={(e) => setExpenseFilterCategory(e.target.value)}
                      >
                        <option value="All">All categories</option>
                        <option value="Clinical supplies">Clinical supplies</option>
                        <option value="Equipment">Equipment</option>
                        <option value="Staff and HR">Staff and HR</option>
                        <option value="Facility">Facility</option>
                        <option value="Marketing">Marketing</option>
                        <option value="Miscellaneous">Miscellaneous</option>
                      </select>
                      <select
                        className="form-control"
                        value={expenseFilterMethod}
                        onChange={(e) => setExpenseFilterMethod(e.target.value)}
                      >
                        <option value="All">All methods</option>
                        <option value="Cash">Cash</option>
                        <option value="Card">Card</option>
                        <option value="UPI">UPI</option>
                        <option value="Bank Transfer">Bank Transfer</option>
                      </select>
                    </div>

                    {/* Expenses Table */}
                    {expenses.length === 0 ? (
                      <p style={{ fontSize: "13px", color: "var(--text-secondary)", margin: 0, padding: "20px 0", textAlign: "center" }}>
                        No expenditures recorded yet.
                      </p>
                    ) : (
                      <div className="expenses-table-wrapper" style={{ overflowX: "auto", flexGrow: "1" }}>
                        <table className="admin-table text-left-table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Category</th>
                              <th>Description</th>
                              <th>Vendor</th>
                              <th>Method</th>
                              <th>Amount</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {expenses
                              .filter((item) => {
                                const term = expenseSearch.toLowerCase();
                                const desc = (item.description || "").toLowerCase();
                                const vend = (item.vendor || "").toLowerCase();
                                const matchesSearch = desc.includes(term) || vend.includes(term);

                                const matchesCategory =
                                  expenseFilterCategory === "All" || item.category === expenseFilterCategory;

                                const matchesMethod =
                                  expenseFilterMethod === "All" || item.paymentMethod === expenseFilterMethod;

                                return matchesSearch && matchesCategory && matchesMethod;
                              })
                              .map((item) => {
                                const badge = getCategoryBadgeDetails(item.category);
                                // Format date like "02 Aug"
                                let shortDate = item.date;
                                if (item.date) {
                                  const parts = item.date.split("-");
                                  if (parts.length === 3) {
                                    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                                    const day = parts[2];
                                    const mIdx = parseInt(parts[1], 10) - 1;
                                    if (mIdx >= 0 && mIdx < 12) {
                                      shortDate = `${day} ${months[mIdx]}`;
                                    }
                                  }
                                }
                                return (
                                  <tr key={item.id}>
                                    <td style={{ whiteSpace: "nowrap" }}>{shortDate}</td>
                                    <td>
                                      <span className={`pill-badge ${badge.className}`}>
                                        {badge.label}
                                      </span>
                                    </td>
                                    <td>
                                      <div className="log-desc-group">
                                        <div className="log-desc-title">{item.description || "-"}</div>
                                        <div className="log-desc-sub">{item.subCategory || ""}</div>
                                        <div className="log-desc-ref">Ref: {item.reference || "None"}</div>
                                      </div>
                                    </td>
                                    <td>{item.vendor || "-"}</td>
                                    <td>{item.paymentMethod || "-"}</td>
                                    <td style={{ fontWeight: 700 }}>
                                      ₹{(parseFloat(item.amount) || 0).toLocaleString("en-IN")}
                                    </td>
                                    <td style={{ width: "40px", textAlign: "right" }}>
                                      <button
                                        className="delete-icon-outline-btn"
                                        onClick={() => handleDeleteExpense(item.id)}
                                        title="Delete Record"
                                      >
                                        <i className="ti ti-trash"></i>
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Filtered total footer */}
                    <div className="filtered-total-summary-row">
                      <span className="total-summary-label">Filtered total</span>
                      <span className="total-summary-value">
                        ₹{
                          expenses
                            .filter((item) => {
                              const term = expenseSearch.toLowerCase();
                              const desc = (item.description || "").toLowerCase();
                              const vend = (item.vendor || "").toLowerCase();
                              const matchesSearch = desc.includes(term) || vend.includes(term);
                              const matchesCategory = expenseFilterCategory === "All" || item.category === expenseFilterCategory;
                              const matchesMethod = expenseFilterMethod === "All" || item.paymentMethod === expenseFilterMethod;
                              return matchesSearch && matchesCategory && matchesMethod;
                            })
                            .reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0)
                            .toLocaleString("en-IN")
                        }
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}

            {accountsSubTab === "monthly" && (
              <div className="monthly-sub-tab-wrapper" style={{ marginTop: "1.5rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
                <div className="grid-card">
                  <h3 className="card-title">Monthly Accounts Summary</h3>
                  <div className="expenses-table-wrapper" style={{ overflowX: "auto", marginTop: "1rem" }}>
                    <table className="admin-table text-left-table">
                      <thead>
                        <tr>
                          <th>Month</th>
                          <th>Patient Income</th>
                          <th>Expenditures</th>
                          <th>Net Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(monthlyGroupedSummary)
                          .sort((a, b) => b[0].localeCompare(a[0]))
                          .map(([monthKey, data]) => {
                            let formattedMonth = monthKey;
                            if (monthKey !== "Unknown") {
                              const parts = monthKey.split("-");
                              const d = new Date(parts[0], parseInt(parts[1]) - 1, 1);
                              formattedMonth = d.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
                            }
                            const netBalance = data.income - data.expenditure;
                            const netColor = netBalance >= 0 ? "var(--green)" : "var(--red)";
                            return (
                              <tr key={monthKey}>
                                <td style={{ fontWeight: 600 }}>{formattedMonth}</td>
                                <td style={{ color: "var(--green)", fontWeight: 700 }}>
                                  ₹{data.income.toLocaleString("en-IN")}
                                </td>
                                <td style={{ color: "#eb6834", fontWeight: 700 }}>
                                  ₹{data.expenditure.toLocaleString("en-IN")}
                                </td>
                                <td style={{ color: netColor, fontWeight: 700 }}>
                                  {netBalance >= 0 ? "+" : ""}₹{netBalance.toLocaleString("en-IN")}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Graph of Income vs Expenditures */}
                <div className="grid-card">
                  <h3 className="card-title" style={{ marginBottom: "6px" }}>Income vs Expenditure Comparison</h3>
                  <p style={{ fontSize: "13px", color: "var(--text-secondary)", marginBottom: "1.5rem" }}>
                    6-month comparative graph showing total patient revenue generated vs operational expenditures.
                  </p>
                  <div className="chart-wrapper" style={{ height: "320px", position: "relative" }}>
                    <canvas id="incomeVsExpenditureChart"></canvas>
                  </div>
                </div>
              </div>
            )}

            {accountsSubTab === "income" && (
              <div className="income-sub-tab-wrapper" style={{ marginTop: "1.5rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
                {/* 4-column metric cards row */}
                <div className="accounts-metric-cards-grid">
                  <div className="accounts-metric-card" style={{ borderLeft: "4px solid var(--green)" }}>
                    <div className="accounts-metric-header">
                      <i className="ti ti-currency-rupee green-icon"></i>
                      <span>Total Patient Income (Paid)</span>
                    </div>
                    <div className="accounts-metric-value">₹{incomeStats.totalIncome.toLocaleString("en-IN")}</div>
                    <div className="accounts-metric-subtext">All time cash collected</div>
                  </div>

                  <div className="accounts-metric-card" style={{ borderLeft: "4px solid var(--primary-blue)" }}>
                    <div className="accounts-metric-header">
                      <i className="ti ti-sum blue-icon"></i>
                      <span>Total Expected Income</span>
                    </div>
                    <div className="accounts-metric-value">₹{(incomeStats.totalIncome + incomeStats.totalDues).toLocaleString("en-IN")}</div>
                    <div className="accounts-metric-subtext">Paid collected + outstanding dues</div>
                  </div>

                  <div className="accounts-metric-card" style={{ borderLeft: "4px solid var(--orange)" }}>
                    <div className="accounts-metric-header">
                      <i className="ti ti-calendar orange-icon"></i>
                      <span>Income This Month</span>
                    </div>
                    <div className="accounts-metric-value">₹{incomeStats.monthIncome.toLocaleString("en-IN")}</div>
                    <div className="accounts-metric-subtext">Current calendar month</div>
                  </div>

                  <div className="accounts-metric-card" style={{ borderLeft: "4px solid var(--red)" }}>
                    <div className="accounts-metric-header">
                      <i className="ti ti-clock red-icon"></i>
                      <span>Outstanding Dues</span>
                    </div>
                    <div className="accounts-metric-value">₹{incomeStats.totalDues.toLocaleString("en-IN")}</div>
                    <div className="accounts-metric-subtext">Unpaid patient balances</div>
                  </div>
                </div>

                {/* Live income transactions list */}
                <div className="grid-card">
                  <h3 className="card-title" style={{ marginBottom: "1rem" }}>Patient Revenue Logs</h3>
                  {incomeStats.incomeTransactions.length === 0 ? (
                    <p style={{ fontSize: "13px", color: "var(--text-secondary)", margin: 0, padding: "12px 0" }}>
                      No patient payments recorded yet.
                    </p>
                  ) : (
                    <div className="expenses-table-wrapper" style={{ overflowX: "auto" }}>
                      <table className="admin-table text-left-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Patient Name</th>
                            <th>Procedure</th>
                            <th>Paid Amount</th>
                            <th>Outstanding Due</th>
                          </tr>
                        </thead>
                        <tbody>
                          {incomeStats.incomeTransactions.map((item) => (
                            <tr key={item.id}>
                              <td>{formatDateSafe(item.date)}</td>
                              <td style={{ fontWeight: 600 }}>{item.name}</td>
                              <td>{item.procedure}</td>
                              <td style={{ color: "var(--green)", fontWeight: 700 }}>
                                ₹{item.paid.toLocaleString("en-IN")}
                              </td>
                              <td style={{ color: item.due > 0 ? "var(--orange)" : "var(--text-secondary)", fontWeight: 600 }}>
                                ₹{item.due.toLocaleString("en-IN")}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
